import { useEffect } from 'react'
import type { GetServerSidePropsContext } from 'next'
import dynamic from 'next/dynamic'
import PropTypes from 'prop-types'
import { BogoBanner } from '@mc/bogo'
import { Separator } from '@mc/design-system'
import { getBucket } from '@mc/next-growthbook'
import { logger } from '@mc/next-logger'
import { PAGE_SOURCES, PromoExplainerModal } from '@mc/promos'
import { StaticDataProvider } from '@mc/static-data-react'
import { TRACK_PROPS } from '@mc/track-event'
import { TrackingParamsProvider } from '@mc/tracking-context'
import { CategoryNavCarousel } from 'components/CategoryNavCarousel'
import GuestPassBanner from 'components/GuestPassBanner'
import { QuizEntryModalLoader } from 'experiments/pen-5646_v1_quiz_entry_sticky_footer/client'
import {
  getModalFeaturedClasses,
  QUIZ_ENTRY_IN_STICKY_FOOTER_EXP_FLAG,
} from 'experiments/pen-5646_v1_quiz_entry_sticky_footer/server'
import { trackEmailCaptureInPromoModalBackendEv } from 'experiments/pen-5891_v1_email_capture_in_promo_modal'
import { articleCategoryPath } from 'libs/paths'
import {
  getArticle,
  getArticleCategories,
  getDefaultProduct,
  getFreeTrialByToken,
  getV2Categories,
} from 'libs/server/api'
import { getPageData } from 'libs/server/page-data'
import { redirect } from 'libs/utils'
import {
  ARTICLE_TYPE,
  CATEGORIES_TYPE,
  FREE_TRIAL_TYPE,
  PRODUCT_PLAN_TYPE,
} from 'types'
import StickyFooter from '../components/StickyFooter'
import Details from './components/Details'
import InstructorCarousel from './components/InstructorsCarousel'
import Intro from './components/Intro'
import Meta from './components/Meta'
import QuizBanner from './components/Quiz'
import {
  trackArticleViewed,
  trackCategoryCarouselClick,
} from './components/events'
import { getRedirectPathForArticle } from './libs/articleRedirects'

const BelowTheFold = dynamic(() => import('./components/BelowTheFold'), {
  ssr: false,
})

type Ret<T extends (...args: never[]) => unknown> = Awaited<ReturnType<T>>

type ArticlePageProps = {
  annualPass: Ret<typeof getDefaultProduct>
  article: Ret<typeof getArticle>
  articleCategories: Ret<typeof getArticleCategories>
  trial: Ret<typeof getFreeTrialByToken>
  hasCompletedQuiz: boolean
  shouldAutoOpenQuiz: boolean
  quizEntryModalFeaturedClasses: Ret<typeof getModalFeaturedClasses>
}

const Article = ({
  annualPass,
  article,
  articleCategories,
  trial,
  hasCompletedQuiz,
  shouldAutoOpenQuiz,
  quizEntryModalFeaturedClasses,
}: ArticlePageProps) => {
  useEffect(() => {
    trackArticleViewed(article)
  }, [article])

  return (
    <StaticDataProvider
      value={{
        annualPass,
        article,
        articleCategories,
        trial,
        quizEntryModalFeaturedClasses,
      }}
      providerKey={article.slug}
    >
      <TrackingParamsProvider
        value={{ [TRACK_PROPS.PAGE_TYPE]: 'next-articles' }}
      >
        <Meta />
        <BogoBanner />
        <GuestPassBanner />
        {!hasCompletedQuiz && (
          <QuizBanner shouldAutoOpenQuiz={shouldAutoOpenQuiz} />
        )}
        <PromoExplainerModal pageSource={PAGE_SOURCES.ARTICLE_PAGE} />
        <QuizEntryModalLoader />

        <Separator className='mc-opacity--muted mc-m-0' />
        <div className='container'>
          <div className='mc-my-2'>
            <CategoryNavCarousel
              categories={articleCategories}
              pathCategory={articleCategoryPath}
              onNavClick={trackCategoryCarouselClick}
            />
          </div>
        </div>
        <div className='mc-theme-light mc-bg-light'>
          <Intro />
          <InstructorCarousel className='col-lg-8 offset-lg-1' />
          <Details />
          <BelowTheFold />
        </div>
        <StickyFooter />
      </TrackingParamsProvider>
    </StaticDataProvider>
  )
}

Article.propTypes = {
  article: ARTICLE_TYPE,
  articleCategories: CATEGORIES_TYPE,
  annualPass: PRODUCT_PLAN_TYPE,
  trial: FREE_TRIAL_TYPE,
  hasCompletedQuiz: PropTypes.bool,
  instructors: PropTypes.array,
  shouldAutoOpenQuiz: PropTypes.bool,
}

Article.pageSettings = {
  analyticsLabel: 'Article Page',
  showPromo: true,
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const {
    query: { slug },
    req: {
      cookies: {
        referral_token: referralToken,
        'quiz-completed': quizCompleted,
      },
      headers: { host, referer },
    },
  } = ctx

  if (typeof slug !== 'string') {
    logger.error('Invalid slug', { slug })
    throw new Error('Invalid slug')
  }
  const redirectLocation = getRedirectPathForArticle(slug)
  if (redirectLocation) {
    return redirect(ctx, redirectLocation, { queryExclude: ['slug'] })
  }

  const hasCompletedQuiz = quizCompleted === 'true'
  const hasMCReferrer = referer && referer.indexOf(host || '') !== -1
  const shouldAutoOpenQuiz = !!hasMCReferrer && !hasCompletedQuiz

  const quizEntryExperimentBucket = await getBucket(
    ctx,
    QUIZ_ENTRY_IN_STICKY_FOOTER_EXP_FLAG,
  )

  const shouldFetchQuizEntryClasses =
    quizEntryExperimentBucket?.startsWith('variant')

  await trackEmailCaptureInPromoModalBackendEv(ctx)

  return getPageData({
    criticalPromises: {
      article: getArticle(ctx, slug),
    },
    nonCriticalPromises: {
      annualPass: getDefaultProduct(ctx),
      articleCategories: getArticleCategories(ctx),
      categories: getV2Categories(ctx),
      trial: getFreeTrialByToken(ctx, { referralToken }),
      quizEntryModalFeaturedClasses: shouldFetchQuizEntryClasses
        ? getModalFeaturedClasses(ctx)
        : Promise.resolve([]),
    },
    propsMapper: ({ ...restProps }) => ({
      hasCompletedQuiz: hasCompletedQuiz || false,
      shouldAutoOpenQuiz,
      ...restProps,
    }),
  })
}

export default Article
